// Shared styles for the ACON-60 marketplace list views. Mirrors the visual
// language of MyOrders/MyRFQs (cards, filter row, pager) so the new tabs feel
// native to the existing marketplace.

export const listStyles = {
  page: { minHeight: '100vh', backgroundColor: 'var(--background)', color: 'var(--foreground)' },
  main: { padding: '24px 24px' },
  container: { maxWidth: 1100, margin: '0 auto' },
  err: { color: 'var(--destructive)', fontSize: 14 },
  muted: { color: 'var(--muted-foreground)', fontSize: 14 },
  emptyCard: {
    backgroundColor: 'var(--card)',
    border: '1px solid var(--border)',
    borderRadius: 12,
    padding: 24,
  },
  refreshBtn: {
    border: '1px solid #d1d5db', background: 'transparent', color: '#374151',
    borderRadius: 8, padding: '8px 14px', fontWeight: 600, cursor: 'pointer', fontSize: 13,
  },

  filterRow: { display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 12, marginBottom: 16 },
  searchWrap: { position: 'relative', flex: '1 1 240px', minWidth: 200, display: 'flex', alignItems: 'center' },
  searchInput: {
    width: '100%', boxSizing: 'border-box', padding: '8px 12px', borderRadius: 8,
    border: '1px solid var(--border)', fontSize: 13, backgroundColor: 'var(--card)', color: 'var(--foreground)',
  },
  sortSelect: {
    padding: '8px 12px', borderRadius: 8, border: '1px solid var(--border)', fontSize: 13,
    backgroundColor: 'var(--card)', color: 'var(--foreground)',
  },

  // ACON-65: collapsible filter panel + sortable column headers, shared by
  // Invoices/Shipments (and available to any other list view going forward).
  searchRow: { display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 12, marginBottom: 12 },
  filtersToggleBtn: { display: 'flex', alignItems: 'center', gap: 6, border: '1px solid var(--border)', background: 'var(--card)', color: 'var(--foreground)', borderRadius: 8, padding: '8px 14px', fontWeight: 600, fontSize: 13, cursor: 'pointer', whiteSpace: 'nowrap' },
  filtersToggleBtnActive: { display: 'flex', alignItems: 'center', gap: 6, border: '1px solid var(--primary)', background: 'var(--secondary)', color: 'var(--foreground)', borderRadius: 8, padding: '8px 14px', fontWeight: 600, fontSize: 13, cursor: 'pointer', whiteSpace: 'nowrap' },
  // Field widths below are deliberately tight: all four fields plus the Clear/Apply
  // buttons have to fit the 1068px of usable width inside the 1100px page container,
  // otherwise the buttons wrap onto a second line.
  filterPanel: { display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', gap: 12, backgroundColor: 'var(--secondary)', border: '1px solid var(--border)', borderRadius: 10, padding: '14px 16px', marginBottom: 16 },
  filterField: { display: 'flex', flexDirection: 'column', gap: 4, minWidth: 150 },
  // Status needs less room than the default field width; the space goes to the date range.
  filterFieldCompact: { display: 'flex', flexDirection: 'column', gap: 4, minWidth: 128 },
  compactSelect: {
    width: 128, boxSizing: 'border-box', padding: '8px 12px', borderRadius: 8,
    border: '1px solid var(--border)', fontSize: 13, backgroundColor: 'var(--card)', color: 'var(--foreground)',
  },
  // Bounded so a long option ('Multiple Work Orders', 'Inventory Transfer') can't
  // size the select past its share of the row; the dropdown still shows full text.
  filterSelect: {
    width: 150, boxSizing: 'border-box', padding: '8px 12px', borderRadius: 8,
    border: '1px solid var(--border)', fontSize: 13, backgroundColor: 'var(--card)', color: 'var(--foreground)',
  },
  filterLabel: { fontSize: 11, fontWeight: 600, color: 'var(--muted-foreground)', textTransform: 'uppercase', letterSpacing: '0.04em' },
  rangeRow: { display: 'flex', alignItems: 'center', gap: 6 },
  // Sized to hold at least 6 digits: the number spinner claims ~16px of the content
  // box, so the usable text area is narrower than the width suggests.
  rangeInput: { width: 96, boxSizing: 'border-box', padding: '8px 8px', borderRadius: 8, border: '1px solid var(--border)', fontSize: 13, backgroundColor: 'var(--card)', color: 'var(--foreground)' },
  // Wider than rangeInput: a date input has to fit yyyy-mm-dd plus the native picker icon,
  // which the numeric width clips once a date is actually selected.
  dateRangeInput: { width: 132, boxSizing: 'border-box', padding: '8px 8px', borderRadius: 8, border: '1px solid var(--border)', fontSize: 13, backgroundColor: 'var(--card)', color: 'var(--foreground)' },
  filterActions: { display: 'flex', alignItems: 'center', gap: 8, marginLeft: 'auto' },
  applyFiltersBtn: { border: '1px solid var(--primary)', background: 'var(--primary)', color: 'var(--primary-foreground)', borderRadius: 8, padding: '8px 16px', fontWeight: 600, fontSize: 13, cursor: 'pointer', whiteSpace: 'nowrap' },
  applyFiltersBtnDisabled: { border: '1px solid var(--border)', background: 'var(--muted)', color: 'var(--muted-foreground)', borderRadius: 8, padding: '8px 16px', fontWeight: 600, fontSize: 13, cursor: 'not-allowed', whiteSpace: 'nowrap' },
  clearFiltersBtn: { border: '1px solid var(--border)', background: 'transparent', color: 'var(--foreground)', borderRadius: 8, padding: '8px 14px', fontWeight: 600, fontSize: 13, cursor: 'pointer', whiteSpace: 'nowrap' },
  clearFiltersBtnDisabled: { border: '1px solid var(--border)', background: 'transparent', color: 'var(--muted-foreground)', borderRadius: 8, padding: '8px 14px', fontWeight: 600, fontSize: 13, cursor: 'not-allowed', whiteSpace: 'nowrap' },
  toggleLabel: { display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 500, color: 'var(--foreground)', whiteSpace: 'nowrap', cursor: 'pointer' },
  sortableTh: { textAlign: 'left', padding: '12px 14px', backgroundColor: 'var(--muted)', fontWeight: 600, borderBottom: '2px solid var(--border)', whiteSpace: 'nowrap', cursor: 'pointer', userSelect: 'none' },
  sortArrow: { fontSize: 10, marginLeft: 4 },

  pager: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 16, marginTop: 20 },
  pagerBtn: {
    border: '1px solid #d1d5db', background: 'transparent', color: '#374151',
    borderRadius: 8, padding: '6px 14px', fontWeight: 600, fontSize: 13, cursor: 'pointer',
  },
  pagerBtnDisabled: {
    border: '1px solid #e5e7eb', background: '#f3f4f6', color: '#9ca3af',
    borderRadius: 8, padding: '6px 14px', fontWeight: 600, fontSize: 13, cursor: 'not-allowed',
  },
  pagerInfo: { fontSize: 13, color: 'var(--muted-foreground)', fontWeight: 500 },

  // Flat table (Invoices, Shipments)
  tableWrap: {
    overflowX: 'auto', backgroundColor: 'var(--card)', border: '1px solid var(--border)', borderRadius: 12,
  },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
  th: {
    textAlign: 'left', padding: '12px 14px', backgroundColor: 'var(--muted)', fontWeight: 600,
    borderBottom: '2px solid var(--border)', whiteSpace: 'nowrap',
  },
  td: { padding: '12px 14px', borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap' },
  rowEven: { backgroundColor: 'var(--secondary)' },

  statusBadge: { display: 'inline-block', borderRadius: 6, padding: '3px 10px', fontSize: 12, fontWeight: 600 },

  // Card + collapsible (RMAs)
  card: {
    backgroundColor: 'var(--card)', border: '1px solid var(--border)', borderRadius: 12,
    marginBottom: 12, overflow: 'hidden',
  },
  cardHeader: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '16px 20px', cursor: 'pointer', gap: 12, flexWrap: 'wrap', userSelect: 'none',
  },
  cardLeft: { display: 'flex', alignItems: 'center', gap: 10 },
  cardName: { fontWeight: 700, fontSize: 15 },
  cardRight: { display: 'flex', alignItems: 'center', gap: 16 },
  linesHint: { fontSize: 12, fontWeight: 600, color: 'var(--muted-foreground)' },
  expandChevron: { display: 'flex', alignItems: 'center', color: 'var(--muted-foreground)', transition: 'transform 0.18s ease' },
  detailRow: { display: 'flex', flexWrap: 'wrap', gap: 24, padding: '0 20px 14px' },
  detailItem: { display: 'flex', flexDirection: 'column', gap: 2, minWidth: 100 },
  detailLabel: { fontSize: 11, fontWeight: 600, color: 'var(--muted-foreground)', textTransform: 'uppercase', letterSpacing: '0.04em' },
  detailValue: { fontSize: 14, fontWeight: 500, color: 'var(--foreground)' },
  cardBody: { borderTop: '1px solid var(--border)', padding: '16px 20px' },
  lineTableWrap: { overflowX: 'auto' },
  lineTable: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
  lineTh: {
    textAlign: 'left', padding: '8px 10px', backgroundColor: 'var(--muted)', fontWeight: 600,
    borderBottom: '2px solid var(--border)', whiteSpace: 'nowrap',
  },
  lineTd: { padding: '8px 10px', borderBottom: '1px solid var(--border)' },
  lineRowEven: { backgroundColor: 'var(--secondary)' },
};

// A neutral status badge. The Core objects (Invoice/RMA/Release) each have their
// own status picklists, so rather than hard-code every value we colour by a small
// set of common semantic buckets and fall back to grey.
const GREEN = { color: '#059669', bg: '#ecfdf5' };
const BLUE = { color: '#2563eb', bg: '#eff6ff' };
const AMBER = { color: '#d97706', bg: '#fffbeb' };
const RED = { color: '#dc2626', bg: '#fef2f2' };
const GREY = { color: '#6b7280', bg: '#f3f4f6' };

const STATUS_BUCKETS = [
  { match: /(cancel|reject|void|hold)/i, ...RED },
  { match: /(open|new|pending|draft|await|progress|created)/i, ...BLUE },
  { match: /(ship|complete|closed|paid|invoiced|received|approved|done)/i, ...GREEN },
  { match: /(sent|partial|process|allocat)/i, ...AMBER },
];

export function statusStyle(status) {
  const s = (status || '').replace(/_/g, ' ').trim();
  if (!s) return { label: '—', ...GREY };
  for (const b of STATUS_BUCKETS) {
    if (b.match.test(s)) return { label: s, color: b.color, bg: b.bg };
  }
  return { label: s, ...GREY };
}

export function formatDate(value) {
  if (!value) return '—';
  const d = new Date(value);
  return isNaN(d.getTime()) ? '—' : d.toLocaleDateString();
}
